import { X } from "lucide-react";

/**
 * Horizontal strip of currently-open pages, shown above the page content.
 *
 * Expected shape of `openTabs`: an array where Dashboard is always the first
 * entry and is never closable. Each entry can be either:
 *   - a plain string key, e.g. "salesOrder"
 *   - an object, e.g. { key: "salesOrder", label: "SalesOrder" }
 * If your AppContext stores tabs differently, adjust `getKey`/`getLabel` below
 * to match — everything else in this file only depends on those two helpers.
 *
 * Props:
 *   openTabs  - array of open tab entries (Dashboard first, non-closable)
 *   activeTab - key of the tab currently shown in the content area
 *   onSelect  - (key) => void, called when a tab is clicked
 *   onClose   - (key) => void, called when a tab's close (×) button is clicked
 */
export default function OpenTabsBar({ openTabs = [], activeTab, onSelect, onClose }) {
  const getKey = (tab) => (typeof tab === "string" ? tab : tab.key);

  const getLabel = (tab) => {
    if (typeof tab === "string") return humanize(tab);
    return tab.label || humanize(tab.key);
  };

  // Fallback: turn a camelCase key like "salesOrder" into "Sales Order"
  const humanize = (key) => {
    if (!key) return "";
    if (key === "dashBoard") return "Dashboard";
    return key
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (c) => c.toUpperCase());
  };

  if (!openTabs.length) return null;

  return (
    <div className="open-tabs-bar" role="tablist" aria-label="Open pages">
      {openTabs.map((tab) => {
        const key = getKey(tab);
        const label = getLabel(tab);
        const isDashboard = key === "dashBoard";
        const isActive = key === activeTab;

        return (
          <div
            key={key}
            role="tab"
            aria-selected={isActive}
            className={`open-tab${isActive ? " active" : ""}${
              isDashboard ? " pinned" : ""
            }`}
            onClick={() => onSelect(key)}
            title={label}
          >
            <span className="open-tab-label">{label}</span>

            {/* Dashboard is pinned and can't be closed */}
            {!isDashboard && (
              <button
                type="button"
                className="open-tab-close"
                aria-label={`Close ${label}`}
                onClick={(e) => {
                  e.stopPropagation(); // don't also select the tab we're closing
                  onClose(key);
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
